import {
  type ApiCallOptions,
  useOAuth2,
} from "@mercdev.com/mercury-id-authorization";

type ApiCallFunction = ReturnType<typeof useOAuth2>["apiCall"];

type DeleteOptions = Omit<ApiCallOptions, "method"> & { data?: unknown };

class ApiClient {
  private apiCall: ApiCallFunction | null = null;
  private isInitialized = false;

  initialize(apiCall: ApiCallFunction) {
    this.apiCall = apiCall;
    this.isInitialized = true;
  }

  private getApiCall(): ApiCallFunction {
    if (!this.isInitialized || !this.apiCall) {
      throw new Error("ApiClient not initialized. Call initialize() first.");
    }
    return this.apiCall;
  }

  shortener = {
    get: <T = unknown>(
      endpoint: string,
      options?: Omit<ApiCallOptions, "method">
    ) => this.getApiCall().get<T>(endpoint, options),

    post: <T = unknown>(
      endpoint: string,
      data?: unknown,
      options?: Omit<ApiCallOptions, "method" | "body">
    ) => this.getApiCall().post<T>(endpoint, data, options),

    put: <T = unknown>(
      endpoint: string,
      data?: unknown,
      options?: Omit<ApiCallOptions, "method" | "body">
    ) => this.getApiCall().put<T>(endpoint, data, options),

    patch: <T = unknown>(
      endpoint: string,
      data?: unknown,
      options?: Omit<ApiCallOptions, "method" | "body">
    ) => this.getApiCall().patch<T>(endpoint, data, options),

    delete: <T = unknown>(endpoint: string, options?: DeleteOptions) => {
      if (options && "data" in options && options.data !== undefined) {
        const { data, headers, ...rest } = options;
        return this.getApiCall().delete<T>(endpoint, {
          ...rest,
          headers: {
            "Content-Type": "application/json",
            ...(headers || {}),
          },
          body: JSON.stringify(data),
        });
      }
      return this.getApiCall().delete<T>(endpoint, options);
    },
  };

  fetcher = async <T = unknown>(url: string): Promise<T> => {
    const response = await this.shortener.get<T>(url);
    return response.data;
  };

  getBaseURL = () => {
    return import.meta.env.VITE_SHORTENER_API_URL || "";
  };
}

export const apiClient = new ApiClient();
export const fetcher = <T = unknown>(url: string) => apiClient.fetcher<T>(url);
